import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Badge,
  Banner,
  Button,
  Card,
  DataTable,
  Layout,
  Page,
  Pagination,
  Select,
  Spinner,
  Stack,
  Tabs,
  TextStyle,
  TextField,
} from "@shopify/polaris";
import { fetchData, fetcher, postData } from "util/fetch";
import LetterProgressGrid from "./components/LetterProgressGrid";
import SyncHistoryTable from "./components/SyncHistoryTable";
import MultipackMappingsTable from "./components/MultipackMappingsTable";
import StoreBottleDepositsTable from "./components/StoreBottleDepositsTable";

const LETTERS     = ["#", ...Array.from({ length: 26 }, (_, i) => String.fromCharCode(65 + i))];
const POLL_MS     = 5000;
const STATUS_BADGE = {
  completed:  "success",
  processing: "attention",
  failed:     "critical",
  pending:    "new",
  cancelled:  "warning",
};

const PRODUCT_TYPES = [
  { label: "All types", value: "" },
  { label: "Alcohol",   value: "Alcohol" },
  { label: "Soft Drink", value: "Soft Drink" },
  { label: "Water",     value: "Water" },
  { label: "Others",    value: "Others" },
];

function isActive(status) {
  return status === "pending" || status === "processing";
}

export default function BottleDepositsPage() {
  // ── Sync state ──────────────────────────────────────────────────────────────
  const [latestSync,      setLatestSync]      = useState(undefined);
  const [letterProgress,  setLetterProgress]  = useState(null);
  const [syncs,           setSyncs]           = useState([]);
  const [syncsLoading,    setSyncsLoading]    = useState(true);
  const [triggering,      setTriggering]      = useState(false);
  const [cancelling,      setCancelling]      = useState(false);
  const [syncError,       setSyncError]       = useState(null);
  const pollRef = useRef(null);

  // ── Tabs state ───────────────────────────────────────────────────────────────
  const [selectedTab, setSelectedTab] = useState(0);

  // ── Deposits state ───────────────────────────────────────────────────────────
  const [deposits,        setDeposits]        = useState([]);
  const [depositsLoading, setDepositsLoading] = useState(true);
  const [searchQuery,     setSearchQuery]     = useState("");
  const [productType,     setProductType]     = useState("");
  const [page,            setPage]            = useState(1);
  const [totalPages,      setTotalPages]      = useState(1);
  const [totalDeposits,   setTotalDeposits]   = useState(0);
  const searchTimeout = useRef(null);

  const latestSyncId = latestSync?.id;
  const latestSyncStatus = latestSync?.status;

  // ── Loaders ─────────────────────────────────────────────────────────────────
  const loadLatestSync = useCallback(() => {
    fetcher("/api/bottle_deposit_syncs/latest")
      .then((r) => r.json())
      .then(({ sync }) => setLatestSync(sync));
  }, []);

  const loadSyncHistory = useCallback(() => {
    setSyncsLoading(true);
    fetchData(
      "/api/bottle_deposit_syncs",
      ({ syncs }) => setSyncs(syncs),
      (err) => console.error(err),
      () => setSyncsLoading(false)
    );
  }, []);

  const loadDeposits = useCallback(() => {
    setDepositsLoading(true);
    const params = new URLSearchParams();
    if (searchQuery)  params.set("q", searchQuery);
    if (productType)  params.set("product_type", productType);
    params.set("page", page);

    fetchData(
      `/api/bottle_deposits?${params}`,
      ({ deposits, total, total_pages }) => {
        setDeposits(deposits);
        setTotalDeposits(total);
        setTotalPages(total_pages);
      },
      (err) => console.error(err),
      () => setDepositsLoading(false)
    );
  }, [searchQuery, productType, page]);

  const loadActiveSyncProgress = useCallback(() => {
    if (!latestSyncId) return;
    fetcher(`/api/bottle_deposit_syncs/${latestSyncId}`)
      .then((r) => r.json())
      .then(({ sync, letter_progress }) => {
        setLatestSync(sync);
        setLetterProgress(letter_progress);
        if (!isActive(sync.status)) {
          loadSyncHistory();
          loadDeposits();
        }
      });
  }, [latestSyncId, loadSyncHistory, loadDeposits]);

  // ── Load latest sync + history on mount ────────────────────────────────────
  useEffect(() => {
    loadLatestSync();
    loadSyncHistory();
  }, [loadLatestSync, loadSyncHistory]);

  // ── Polling when sync is active ─────────────────────────────────────────────
  useEffect(() => {
    if (latestSyncStatus && isActive(latestSyncStatus)) {
      pollRef.current = setInterval(loadActiveSyncProgress, POLL_MS);
    } else {
      clearInterval(pollRef.current);
    }
    return () => clearInterval(pollRef.current);
  }, [latestSyncStatus, loadActiveSyncProgress]);

  // ── Load deposits on search/filter/page change ──────────────────────────────
  useEffect(() => {
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      loadDeposits();
    }, 300);
    return () => clearTimeout(searchTimeout.current);
  }, [loadDeposits]);

  // ── Cancel sync ──────────────────────────────────────────────────────────────
  const handleCancel = useCallback(async () => {
    if (!latestSyncId) return;
    setCancelling(true);
    try {
      const res  = await postData(`/api/bottle_deposit_syncs/${latestSyncId}/cancel`);
      const data = await res.json();
      setLatestSync(data.sync);
      loadSyncHistory();
    } catch (err) {
      setSyncError(err.message || "Failed to cancel sync");
    } finally {
      setCancelling(false);
    }
  }, [latestSyncId, loadSyncHistory]);

  // ── Trigger sync ─────────────────────────────────────────────────────────────
  const handleTrigger = useCallback(async () => {
    setSyncError(null);
    setTriggering(true);
    try {
      const res  = await postData("/api/bottle_deposit_syncs");
      const data = await res.json();
      setLatestSync(data.sync);
      setLetterProgress(null);
      loadSyncHistory();
    } catch (err) {
      const message = err.message || "Failed to trigger sync";
      setSyncError(message);
    } finally {
      setTriggering(false);
    }
  }, [loadSyncHistory]);

  // ── Deposits table rows ───────────────────────────────────────────────────────
  const depositRows = deposits.map((d) => [
    d.upc,
    d.brand,
    d.flavour || "—",
    d.product_type,
    d.container_type,
    d.deposit_cents != null ? `$${(d.deposit_cents / 100).toFixed(2)}` : "—",
    d.crf_cents != null ? `$${(d.crf_cents / 100).toFixed(2)}` : "—",
  ]);

  // ── Sync status card ──────────────────────────────────────────────────────────
  const syncStatusCard = latestSync ? (
    <Card
      title="Latest Sync"
      actions={[]}
    >
      <Card.Section>
        <Stack>
          <Badge status={STATUS_BADGE[latestSync.status]}>{latestSync.status}</Badge>
          <TextStyle variation="subdued">
            {latestSync.triggered_by || "—"} · {latestSync.duration_formatted || "in progress"}
          </TextStyle>
        </Stack>
        {isActive(latestSync.status) && (
          <div style={{ marginTop: "8px" }}>
            <Stack alignment="center">
              <Spinner size="small" />
              <TextStyle variation="subdued">
                {latestSync.completed_letters}/{latestSync.total_letters} letters complete
              </TextStyle>
              <Button
                size="slim"
                destructive
                loading={cancelling}
                onClick={handleCancel}
              >
                Cancel
              </Button>
            </Stack>
          </div>
        )}
        {!isActive(latestSync.status) && (
          <div style={{ marginTop: "12px" }}>
            <Stack distribution="fill">
              {[
                ["Scraped",    latestSync.total_scraped],
                ["New",        latestSync.total_inserted],
                ["Updated",    latestSync.total_updated],
                ["Unchanged",  latestSync.total_unchanged],
                ["Errors",     latestSync.total_errors],
              ].map(([label, val]) => (
                <div key={label} style={{ textAlign: "center" }}>
                  <div style={{ fontSize: "20px", fontWeight: 600 }}>{val}</div>
                  <TextStyle variation="subdued">{label}</TextStyle>
                </div>
              ))}
            </Stack>
          </div>
        )}
      </Card.Section>

      {(isActive(latestSync.status) || letterProgress) && (
        <Card.Section title="Letter Progress">
          <LetterProgressGrid
            letters={LETTERS}
            letterProgress={letterProgress}
          />
        </Card.Section>
      )}
    </Card>
  ) : (
    <Card title="Latest Sync">
      <Card.Section>
        <TextStyle variation="subdued">No syncs have run yet.</TextStyle>
      </Card.Section>
    </Card>
  );

  return (
    <Page
      title="Bottle Deposits"
      primaryAction={{
        content:  "Trigger Sync Now",
        onAction: handleTrigger,
        loading:  triggering,
        disabled: triggering || (latestSync && isActive(latestSync.status)),
      }}
    >
      <Tabs
        tabs={[
          { id: 'deposits', content: 'Deposits' },
          { id: 'mappings', content: 'Multipack Mappings' },
          { id: 'store-overrides', content: 'Store Overrides' }
        ]}
        selected={selectedTab}
        onSelect={setSelectedTab}
      >
        <Layout>
          {syncError && (
            <Layout.Section>
              <Banner status="critical" onDismiss={() => setSyncError(null)}>
                {syncError}
              </Banner>
            </Layout.Section>
          )}

          {selectedTab === 0 ? (
            <>
              <Layout.Section>{syncStatusCard}</Layout.Section>

              <Layout.Section>
                <Card title="Sync History">
                  <Card.Section>
                    <SyncHistoryTable syncs={syncs} loading={syncsLoading} />
                  </Card.Section>
                </Card>
              </Layout.Section>

              <Layout.Section>
                <Card title={`Bottle Deposits (${totalDeposits.toLocaleString()})`}>
                  <Card.Section>
                    <Stack>
                      <Stack.Item fill>
                        <TextField
                          label="Search"
                          labelHidden
                          placeholder="Search by brand, UPC, product type…"
                          value={searchQuery}
                          onChange={(val) => { setSearchQuery(val); setPage(1); }}
                          clearButton
                          onClearButtonClick={() => { setSearchQuery(""); setPage(1); }}
                        />
                      </Stack.Item>
                      <Select
                        label="Product type"
                        labelHidden
                        options={PRODUCT_TYPES}
                        value={productType}
                        onChange={(val) => { setProductType(val); setPage(1); }}
                      />
                    </Stack>
                  </Card.Section>

                  <Card.Section flush>
                    {depositsLoading ? (
                      <div style={{ padding: "2rem", textAlign: "center" }}>
                        <Spinner size="small" />
                      </div>
                    ) : deposits.length === 0 ? (
                      <div style={{ padding: "2rem", textAlign: "center" }}>
                        <TextStyle variation="subdued">No deposits found.</TextStyle>
                      </div>
                    ) : (
                      <DataTable
                        columnContentTypes={["text", "text", "text", "text", "text", "text", "text"]}
                        headings={["UPC", "Brand", "Flavour", "Type", "Container", "Deposit", "CRF"]}
                        rows={depositRows}
                      />
                    )}
                  </Card.Section>

                  {totalPages > 1 && (
                    <Card.Section>
                      <Stack distribution="center">
                        <Pagination
                          hasPrevious={page > 1}
                          onPrevious={() => setPage((p) => p - 1)}
                          hasNext={page < totalPages}
                          onNext={() => setPage((p) => p + 1)}
                        />
                      </Stack>
                    </Card.Section>
                  )}
                </Card>
              </Layout.Section>
            </>
          ) : selectedTab === 1 ? (
            <Layout.Section>
              <MultipackMappingsTable />
            </Layout.Section>
          ) : (
            <Layout.Section>
              <StoreBottleDepositsTable />
            </Layout.Section>
          )}
        </Layout>
      </Tabs>
    </Page>
  );
}
